import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {Journal, JournalProps, OperationType} from "../models/journal";
import {Project} from "../models/project";
import {ProjectProcess} from "../models/project-process";


import {Difference} from "../models/difference";
import {Contractor} from "../models/contractor";
import {PartialOrder} from "../models/partial-order";
import {ProjectPerimeter} from "../models/project-perimeter";
import {Parcel} from "../models/parcel";
import {JournalizedType} from "./journalized-type";
import {ProcessSource} from "../models/process-source";
import {Document} from "../models/document";

export interface JournalStoreProps {
    layerConfig: ApiLayerConfiguration;
}

export class JournalStore extends AbstractStore {

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, Journal>();

    constructor(props: JournalStoreProps) {
        super({ layerConfig: props.layerConfig });

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): Journal[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        filtered.sort(Journal.sortByDate);
        return [ ...filtered];
    }

    loadItem(guid: string | undefined): Promise<Journal | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!guid) {
                    reject();
                    return;
                }

                this.queryFeatures([guid]).then((journals: Journal[]) => {
                    if (journals[0] && journals[0].guid === guid) {
                        const journal = journals[0];
                        const loadedJournal = this.items.get(guid);
                        if (loadedJournal) {
                            // return journal as long as merging journal is running. journal can be displayed with
                            // indicator that synching is in journal
                            // TODO: merge journal with already loaded project
                        }

                        this.addItem(journal);
                        this.setSelectedItem(journal);
                        this.setDataStatus("loaded");

                        resolve(journal);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Journal not found");
                    }
                });
            });
        });
    }

    /**
     * Request journals from server and store in local store
     * @param guids (Optional) IDs of journals
     */
    loadItems(guids?: string[]): Promise<Journal[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(guids).then((journals: Journal[]) => {
                    if (journals) {
                        const map = new Map<string, Journal>();
                        journals.forEach((j: Journal) => {
                            map.set(j.guid, j);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(journals);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting journals");
                    }
                });
            });
        });
    }

    journalizeItem(item: Project | ProjectProcess | Contractor | PartialOrder | ProjectPerimeter | Parcel | ProcessSource | Document,
                   journalizedType: JournalizedType,
                   operationType: OperationType,
                   user: string,
                   diff?: Difference[],
                   notes?: string): void {

        // TODO: find a way to store JSON as blob
        let backup = JSON.stringify(diff);
        if (backup?.length > 255) {
            // TODO: find a way to store more data. blob is not accessible through featurelayer
            console.warn("journal data too big.");
            backup = ""; // just store at least the journal entry, avoid errors on save
        }
        let message;
        if (notes) {
            message = notes;
        }
        if (!message) {
            // TODO: message für andere Typen anpassen
            message = journalizedType === "project" ? "Project geändert" : "Projektstatus geändert";
        }

        let journalizedId = "";
        if (item) {
            if (item instanceof Project) {
                const project = item as Project;
                journalizedId = project?.id?.toString() || "";
            }
            else {
                journalizedId = item.guid;
            }
        }

        const journal = new Journal({
            journalizedId,
            guid: crypto.randomUUID(),
            journalizedType,
            operationType,
            notes: message,
            data: backup,
            creator: user,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            createdAt: Date.now()
        });
        this.save(journal, user).then(() => {
            // journal saved successfully
        }, (error) => {
            console.error("Error saving journal", error);
        });
    }

    /**
     * Save journal to the server
     *
     * @param journal
     * @param user
     */
    save(journal: Journal | null,
         user: string): Promise<Journal | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!journal) {
                    reject("Journal undefined when saving edits.");
                    return;
                }

                let feature = journal?.feature;
                if (!feature) {
                    // new journal --> create feature
                    feature = this.createFeature( null, journal);
                }

                this.saveFeature(feature, "add" ,user).then((objectId: number) => {
                    const attrs = {};
                    attrs["objectId"] = objectId;
                    Object.assign(journal, attrs);
                    if (journal.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        journal.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(journal);
                }, (error) => {
                    console.error("Error saving journal", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete a journal
     * @param journal
     */
    delete(journal: Journal | null): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!journal?.guid) {
                    reject("Journal GUID undefined when deleting.");
                    return;
                }

                if (journal.isNewRecord) {
                    reject("This journal is not saved. Does not have to be deleted.");
                    return;
                }

                const feature = journal?.feature;
                if (!feature) {
                    reject(`Feature undefined for journal ${journal.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {

                    this.resetSelectedItem();
                    this.removeItem(journal);

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(journalGuids?: string[]): Promise<Journal[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                // load all features if no journal guid is specified
                if (journalGuids !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where = queryParams.where + ` AND GUID IN ('${journalGuids.join("','")}')`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const journals = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(journals);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying journals + ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): Journal[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const journals: Journal[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, Journal);
            const j = new Journal(json as JournalProps);
            j.feature = feature;

            journals.push(j);
        });
        return journals;
    }

}
